// The detail for one node of the map: a few typed sections, then the raw body for anything the summary
// doesn't name. Engine latency histograms and rsh lanes are the two tables an operator actually reads.

import type { ReactNode } from 'react';
import type { SysHealth } from '@/lib/rustLane/api';
import { fmt, histo } from '@/lib/rustLane/api';
import type { NodeId } from './map';

function KV({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-xs">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="text-foreground break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Raw({ value }: { value: unknown }) {
  return (
    <details className="mt-3">
      <summary className="text-muted-foreground cursor-pointer text-xs">raw</summary>
      <pre className="bg-muted mt-2 max-h-96 overflow-auto rounded p-2 text-[10.5px] leading-snug">{JSON.stringify(value, null, 1)}</pre>
    </details>
  );
}

export function LatencyTable({ latency }: { latency: Record<string, unknown> | null }) {
  if (!latency) return <p className="text-muted-foreground text-xs">no latency section</p>;
  const rows = Object.entries(latency).map(([k, v]) => [k, histo(v)] as const).filter((r) => r[1] !== null);
  return (
    <table className="w-full font-mono text-[11px]">
      <thead><tr className="text-muted-foreground text-left"><th className="pr-2 font-normal">stage</th><th className="pr-2 text-right font-normal">p50</th><th className="pr-2 text-right font-normal">p99</th><th className="pr-2 text-right font-normal">max</th><th className="text-right font-normal">n</th></tr></thead>
      <tbody>
        {rows.map(([k, h]) => (
          <tr key={k} className={h!.n === 0 ? 'text-muted-foreground' : ''}>
            <td className="pr-2">{k}</td>
            <td className="pr-2 text-right">{h!.n ? fmt.us(h!.p50Us) : '—'}</td>
            <td className="pr-2 text-right">{h!.n ? fmt.us(h!.p99Us) : '—'}</td>
            <td className="pr-2 text-right">{h!.n ? fmt.us(h!.maxUs) : '—'}</td>
            <td className="text-right">{fmt.int(h!.n)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Drilldown({ snap, node }: { snap: SysHealth | null; node: NodeId | null }) {
  if (!snap || !node) return <p className="text-muted-foreground text-sm">Select a node on the map.</p>;

  if (node.startsWith('rsh:')) {
    const id = node.slice(4);
    const b = snap.rsh.find((x) => x.id === id);
    if (!b) return <p className="text-muted-foreground text-sm">{id} is not in this snapshot.</p>;
    const s = b.summary;
    const raw = b.raw ?? {};
    const lat = (raw.latency ?? null) as Record<string, unknown> | null;
    const flow = (raw.flow ?? null) as Record<string, unknown> | null;
    const rec = (raw.recorder ?? null) as Record<string, unknown> | null;
    return (
      <div className="space-y-3">
        <h2 className="font-mono text-sm font-semibold">{b.id} <span className="text-muted-foreground font-normal">{b.url}</span></h2>
        <KV rows={[
          ['probe', b.ok ? `ok in ${b.ms} ms` : `${b.error} (${b.ms} ms)`],
          ['rank', `${b.rank + 1} in the floor list`],
          ['ready', s ? String(s.ready) : '—'],
          ['markets / books', s ? `${fmt.int(s.markets)} / ${fmt.int(s.books)}` : '—'],
          ['sports', s ? String(s.sports) : '—'],
          ['consumers', s ? String(s.consumers) : '—'],
          ['incarnation', s?.incarnation ?? '—'],
          ['directory gen', s?.directoryGeneration?.toString() ?? '—'],
          ['up since', s?.startedAtMs ? new Date(s.startedAtMs).toISOString().slice(11, 19) + 'Z' : '—'],
          ['frames out', fmt.int(raw.framesOut)],
          ['dead links / client errors', `${fmt.int(raw.deadLinks)} / ${fmt.int(raw.clientErrors)}`],
        ]} />
        {s && s.lanes.length > 0 && (
          <table className="w-full font-mono text-[11px]">
            <thead><tr className="text-muted-foreground text-left"><th className="font-normal">lane</th><th className="font-normal">state</th><th className="text-right font-normal">claimed</th><th className="text-right font-normal">current</th></tr></thead>
            <tbody>{s.lanes.map((l) => (
              <tr key={l.lane}><td>{l.lane}</td><td className={l.state === 'valid' ? 'text-emerald-400' : 'text-amber-400'}>{l.state}</td><td className="text-right">{fmt.int(l.claimed)}</td><td className="text-right">{fmt.int(l.current)}</td></tr>
            ))}</tbody>
          </table>
        )}
        {lat && <div><h3 className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">latency (box)</h3><KV rows={Object.entries(lat).slice(0, 12).map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)])} /></div>}
        {flow && <div><h3 className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">flow</h3><KV rows={Object.entries(flow).slice(0, 10).map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)])} /></div>}
        {rec && <div><h3 className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">recorder</h3><KV rows={Object.entries(rec).slice(0, 10).map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)])} /></div>}
        <Raw value={raw} />
      </div>
    );
  }

  if (node === 'feedgw') {
    const f = snap.feedgw;
    const s = f.summary;
    return (
      <div className="space-y-3">
        <h2 className="font-mono text-sm font-semibold">feedgw-rst</h2>
        <KV rows={[
          ['probe', f.ok ? `ok in ${f.ms} ms` : `${f.error} (${f.ms} ms)`],
          ['floor publisher', s?.publisher ?? '—'],
          ['floor error', s?.floorError ?? 'none'],
          ['floor edges', s ? s.floorEdges.map((e) => `${e.edgeId}${e.live ? '' : ' (dark)'} ×${e.sports}`).join(', ') || '—' : '—'],
          ['tickers on floor', fmt.int(s?.tickers)],
          ['fleet dup rows dropped', fmt.int(s?.duplicateRowsDropped)],
          ['fallback labels', s ? (s.fallbackLabels.join(', ') || 'none') : '—'],
          ['browser sockets', fmt.int(s?.clients)],
          ['frames out / s', s?.framesOutPerS?.toFixed(1) ?? '—'],
        ]} />
        {s && s.upstreams.length > 0 && (
          <table className="w-full font-mono text-[11px]">
            <thead><tr className="text-muted-foreground text-left"><th className="font-normal">upstream</th><th className="font-normal">tier</th><th className="font-normal">conn</th><th className="text-right font-normal">rows</th><th className="text-right font-normal">books</th><th className="text-right font-normal">last frame</th></tr></thead>
            <tbody>{s.upstreams.map((u) => (
              <tr key={u.id}><td>{u.edgeId ?? u.id}</td><td>{u.tier ?? '—'}</td><td className={u.connected ? 'text-emerald-400' : 'text-red-400'}>{u.connected ? 'yes' : 'no'}</td><td className="text-right">{fmt.int(u.rows)}</td><td className="text-right">{fmt.int(u.books)}</td><td className="text-right">{fmt.ms(u.lastFrameAgeMs)}</td></tr>
            ))}</tbody>
          </table>
        )}
        <Raw value={f.raw} />
      </div>
    );
  }

  if (node === 'engine') {
    const e = snap.engine;
    const s = e.summary;
    return (
      <div className="space-y-3">
        <h2 className="font-mono text-sm font-semibold">g1-engine-rst</h2>
        <KV rows={[
          ['probe', e.ok ? `ok in ${e.ms} ms` : `${e.error} (${e.ms} ms)`],
          ['strategy routes', snap.strategiesConfigured ? 'configured' : 'admin token not set on the relay'],
          ['feed', s?.feed ? `${s.feed.connected ? 'connected' : 'DOWN'} · ${fmt.int(s.feed.markets)} markets · pipes ${fmt.int(s.feed.pipes)} · lanes valid ${fmt.int(s.feed.lanesValid)}` : '—'],
          ['flow', s?.feed ? `${fmt.int(s.feed.flowEvents)} events · ${fmt.int(s.feed.flowGaps)} gaps · clock offset ${fmt.int(s.feed.clockOffsetMs)} ms` : '—'],
          ['account', s?.account ? `${String(s.account.gate)} · cash ${fmt.usd(s.account.cashUsd)} · valid ${String(s.account.valid)}` : '—'],
          ['kill', s?.kill ? JSON.stringify(s.kill) : 'none'],
          ['instances', s ? `${s.instances.length} (${s.instances.filter((i) => i.armed).length} armed, ${s.instances.filter((i) => i.armed && i.mode === 'live').length} live)` : '—'],
        ]} />
        <div><h3 className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">latency</h3><LatencyTable latency={s?.latency ?? null} /></div>
        <Raw value={e.raw} />
      </div>
    );
  }

  if (node === 'relay') {
    return (
      <div className="space-y-3">
        <h2 className="font-mono text-sm font-semibold">relay-rst</h2>
        <div><h3 className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">account hub</h3><Raw value={snap.relay.accountHub} /></div>
        <div><h3 className="text-muted-foreground mb-1 text-xs uppercase tracking-wide">hydra fleet (relay's own sockets)</h3><Raw value={snap.relay.hydraFleet} /></div>
      </div>
    );
  }

  if (node === 'browser') {
    return (
      <div className="space-y-3">
        <h2 className="font-mono text-sm font-semibold">Browser</h2>
        <p className="text-muted-foreground text-xs">The dashboard is static files from nginx. Its rows and books come over one WebSocket per tab from feedgw; games, odds, orders and balance come from the relay over REST and SSE. Nothing in the bundle is Rust.</p>
        <KV rows={[['feed sockets open', fmt.int(snap.feedgw.summary?.clients)], ['frames out / s', snap.feedgw.summary?.framesOutPerS?.toFixed(1) ?? '—']]} />
      </div>
    );
  }

  if (node === 'operator') {
    return <p className="text-muted-foreground text-xs">StreamRift mode drives the engine's admin API through the relay (`/api/admin/rust/strategies/*`). Every change carries your username as the actor and is logged by the engine.</p>;
  }

  return (
    <div className="space-y-3">
      <h2 className="font-mono text-sm font-semibold">Kalshi</h2>
      <p className="text-muted-foreground text-xs">Three surfaces: the market-data WebSocket (one signed connection set per rsh box, four pipes each), public REST discovery (no key, every 60 s), and the trading API the engine's gateway writes to with the rst key. Fills come back on the account WebSocket into the account hub.</p>
    </div>
  );
}
